import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/history.css";

const API_BASE_URL = "https://naxor.naxora.net";

export default function History() {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getHeaders = () => {
    const token = getToken();
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const parseApiResponse = async (response) => {
    const rawText = await response.text();

    let data = null;

    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch (error) {
      data = null;
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      rawText,
    };
  };

  const getApiErrorMessage = (parsed, fallbackMessage) => {
    if (parsed?.data?.message) {
      return parsed.data.message;
    }

    if (parsed?.rawText) {
      if (
        parsed.rawText.includes("<!DOCTYPE") ||
        parsed.rawText.includes("<html")
      ) {
        return `Server returned HTML instead of JSON (HTTP ${parsed.status})`;
      }

      return parsed.rawText;
    }

    return fallbackMessage;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Please login again",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.clear();
    window.location.replace("/login");
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);

      const [depositsRes, withdrawalsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/deposits/my`, {
          method: "GET",
          headers: getHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/withdrawals/my`, {
          method: "GET",
          headers: getHeaders(),
        }),
      ]);

      const [depositsParsed, withdrawalsParsed] = await Promise.all([
        parseApiResponse(depositsRes),
        parseApiResponse(withdrawalsRes),
      ]);

      if (
        depositsRes.status === 401 ||
        depositsRes.status === 403 ||
        withdrawalsRes.status === 401 ||
        withdrawalsRes.status === 403
      ) {
        await handleUnauthorized();
        return;
      }

      if (!depositsParsed.ok) {
        throw new Error(
          getApiErrorMessage(depositsParsed, "Failed to load deposits")
        );
      }

      if (!withdrawalsParsed.ok) {
        throw new Error(
          getApiErrorMessage(withdrawalsParsed, "Failed to load withdrawals")
        );
      }

      const depositItems = Array.isArray(depositsParsed.data?.deposits)
        ? depositsParsed.data.deposits.map((item) => ({
            ...item,
            historyType: "deposit",
            historyDate:
              item.createdAt ||
              item.date ||
              item.requestedAt ||
              item.updatedAt ||
              null,
          }))
        : [];

      const withdrawalItems = Array.isArray(
        withdrawalsParsed.data?.withdrawals
      )
        ? withdrawalsParsed.data.withdrawals.map((item) => ({
            ...item,
            historyType: "withdraw",
            historyDate:
              item.createdAt ||
              item.date ||
              item.requestedAt ||
              item.updatedAt ||
              null,
          }))
        : [];

      setDeposits(depositItems);
      setWithdrawals(withdrawalItems);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to load history",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const historyItems = useMemo(() => {
    return [...deposits, ...withdrawals]
      .filter((item) => {
        const keyword = search.toLowerCase().trim();

        const matchType =
          typeFilter === "all" || item.historyType === typeFilter;

        const matchStatus =
          statusFilter === "all" ||
          String(item.status || "").toLowerCase() === statusFilter;

        const matchSearch =
          !keyword ||
          String(item.walletAddress || "")
            .toLowerCase()
            .includes(keyword) ||
          String(item.txId || item.transactionId || item.id || "")
            .toLowerCase()
            .includes(keyword) ||
          String(item.method || item.network || "")
            .toLowerCase()
            .includes(keyword);

        return matchType && matchStatus && matchSearch;
      })
      .sort((a, b) => {
        const dateA = a.historyDate ? new Date(a.historyDate).getTime() : 0;
        const dateB = b.historyDate ? new Date(b.historyDate).getTime() : 0;
        return dateB - dateA;
      });
  }, [deposits, withdrawals, typeFilter, statusFilter, search]);

  const getAmount = (item) => {
    if (item.historyType === "withdraw") {
      return Number(item.requestedAmount || item.amount || 0).toFixed(2);
    }

    return Number(item.amount || 0).toFixed(2);
  };

  const getTypeLabel = (item) => {
    return item.historyType === "deposit" ? "Deposit" : "Withdraw";
  };

  const getDateLabel = (item) => {
    if (!item.historyDate) return "-";

    const date = new Date(item.historyDate);

    if (Number.isNaN(date.getTime())) return item.historyDate;

    return date.toLocaleString();
  };

  return (
    <div className="admin-deposits-page">

      <div className="withdraw-topbar">
        <Link to="/dashboard" className="withdraw-back-btn">
          ← Back
        </Link>
      </div>

      <h2>History</h2>

      <div style={{ margin: "15px 0", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <input
          placeholder="Search wallet / tx / id / network"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="deposit">Deposit</option>
          <option value="withdraw">Withdraw</option>
        </select>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="admin-deposits-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Wallet / TX</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {historyItems.length === 0 ? (
              <tr>
                <td colSpan="6">No history found</td>
              </tr>
            ) : (
              historyItems.map((item) => (
                <tr key={`${item.historyType}-${item.id}`}>
                  <td>{getTypeLabel(item)}</td>
                  <td>${getAmount(item)}</td>
                  <td>{item.method || item.network || "-"}</td>
                  <td>
                    {item.walletAddress ||
                      item.txId ||
                      item.transactionId ||
                      "-"}
                  </td>
                  <td>{item.status || "-"}</td>
                  <td>{getDateLabel(item)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}